import React, { useState } from 'react';
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  Grid,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import Page from 'src/components/Page';
import Scrollbar from 'src/components/Scrollbar';
import SearchNotFound from 'src/components/SearchNotFound';
import { UserListHead } from 'src/sections/@dashboard/user';
import { useNavigate } from 'react-router-dom';
import { toast, Bounce } from 'react-toastify';
import moment from 'moment';
import palette from 'src/theme/palette';
import { CiSearch } from 'react-icons/ci';
import CustomPagination from 'src/components/CustomPagination';
import { Select } from 'src/components';
import { useAuth } from 'src/context/auth/AuthContext';
import { studentService } from 'src/services/student.service';
import { financialSimulationService } from 'src/services/financial-simulation.service';
import { EyeIcon } from 'src/images';

import 'react-toastify/dist/ReactToastify.css';
import './../../styles/users.css';

interface TableHeadItem {
  id: string;
  label: string;
  alignRight: boolean;
}

const TABLE_HEAD: TableHeadItem[] = [
  { id: 'scenario', label: 'Scenario', alignRight: false },
  { id: 'duration', label: 'Duration', alignRight: false },
  { id: 'amount', label: 'Amount', alignRight: false },
  { id: 'endDate', label: 'End Date', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'actions', label: 'Actions', alignRight: false },
];

const statusOptions = [
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Overdue', label: 'Overdue' },
];

export default function StudentFinancialSimulations(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(8);
  const [searchText, setSearchText] = useState<string>('');
  const [status, setStatus] = useState<string | null>(null);

  // Get student ID from user ID
  const {
    data: studentId,
    isLoading: isLoadingStudent,
    error: studentError,
  } = useQuery({
    queryKey: ['student', user?.id],
    queryFn: () => {
      if (!user?.id) throw new Error('User ID is required');
      return studentService.getStudentByUserId(user.id);
    },
    enabled: !!user?.id,
  });

  // Fetch financial simulations data
  const {
    data: simulations,
    isLoading: isLoadingSimulations,
    error: simulationsError,
  } = useQuery({
    queryKey: ['studentSimulations', page, rowsPerPage, searchText, status, studentId],
    queryFn: async () => {
      return financialSimulationService.getChallenges({
        page,
        limit: rowsPerPage,
        search: searchText,
        status: status as 'In Progress' | 'Completed' | 'Overdue' | null,
        studentId: studentId,
      });
    },
    enabled: !!studentId,
  });

  const isLoading = isLoadingStudent || isLoadingSimulations;
  const error = studentError || simulationsError;

  const handlePageChange = (newPage: number): void => setPage(newPage);

  const handleRowsPerPageChange = (newRowsPerPage: number): void => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const handleStatusChange = (selectedOption: string | null): void => {
    setStatus(selectedOption);
    setPage(1);
  };

  const showErrorToast = (message: string): void => {
    toast.error(message, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'light',
      transition: Bounce,
    });
  };

  // Show error toast if there's an error
  React.useEffect(() => {
    if (error) {
      showErrorToast(error instanceof Error ? error.message : 'An error occurred');
    }
  }, [error]);

  return (
    <Page title="Financial Simulations">
      <Container maxWidth="xl">
        <Card style={{ borderRadius: 16 }}>
          <Grid container spacing={2} style={{ padding: 20 }}>
            <Grid item sm={12} xs={12} md={6} lg={6} xl={6}>
              <div className="_login_row" style={{ backgroundColor: palette.grey[400], borderRadius: 12 }}>
                <CiSearch size={24} color={palette.text.primary} />
                <input
                  type="text"
                  placeholder="Search by Scenario"
                  className="_auth_fields searchField"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
            </Grid>
            <Grid item sm={12} xs={12} md={6} lg={6} xl={6}>
              <Select
                options={statusOptions}
                placeholder="Status"
                firstItem="All Status"
                value={status}
                onChange={handleStatusChange}
              />
            </Grid>
          </Grid>

          <Scrollbar>
            <TableContainer>
              {isLoading ? (
                <div className="_loading_section">
                  <CircularProgress color="primary" />
                </div>
              ) : (
                <Table>
                  <UserListHead headLabel={TABLE_HEAD} rowCount={simulations?.total || 0} />
                  <TableBody>
                    {simulations?.data?.map((row) => (
                      <TableRow hover key={row.id} style={{ fontSize: 20, fontWeight: '600' }}>
                        <TableCell scope="row">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography color="#111827" variant="subtitle1">
                              {row.scenario?.title || '--'}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="left" style={{ marginLeft: 5 }}>
                          <Typography color={palette.text.primary} variant="subtitle1">
                            {row.duration ? `${row.duration} Days` : '--'}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography color={palette.text.primary} variant="subtitle1">
                            {row.amount ?? '--'}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography color={palette.text.primary} variant="subtitle1">
                            {row.duration
                              ? moment(row.createdat).add(row.duration, 'days').format('DD MMM YYYY')
                              : '--'}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <div
                            className={`_loan_status ${
                              row.status === 'In Progress'
                                ? 'status_pending'
                                : row.status === 'Overdue'
                                ? 'status_denied'
                                : row.status === 'Completed'
                                ? 'status_approved'
                                : ''
                            }`}
                          >
                            <Typography variant="subtitle2">{row.status || '--'}</Typography>
                          </div>
                        </TableCell>
                        <TableCell align="left">
                          <IconButton onClick={() => navigate(`/student/view-financial-simulations/${row.id}`)}>
                            <img src={EyeIcon} alt="View" style={{ cursor: 'pointer' }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>

                  {(!simulations?.data?.length || simulations.total === 0) && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                          <SearchNotFound searchQuery={searchText} />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              )}
            </TableContainer>
          </Scrollbar>

          {simulations && simulations.total > 0 && (
            <CustomPagination
              count={simulations.total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          )}
        </Card>
      </Container>

    </Page>
  );
}
